import {
  generateImageMarkup,
  generateVideoMarkup,
  generateExternalVideoMarkup,
  isMobile,
} from '@hh/helper-functions'

const KEYCODE_UP = 38
const KEYCODE_DOWN = 40

class SwiperProductGallery extends HTMLElement {
  variantImagesHTMLCACHE = {}

  constructor() {
    super()

    this.handleVariantChange = this.handleVariantChange.bind(this)
    this.mainProduct = this.closest('main-product')

    this.swiperMainDesktop = this.querySelector(
      '.product-gallery__main.desktop'
    )
    this.swiperMainMobile = this.querySelector('.product-gallery__main.mobile')
    this.swiperContainers = [...this.querySelectorAll('swiper-container')]

    this.zoomButtons = [
      ...this.querySelectorAll('.adaptive-image__icon.js-zoom'),
    ]
    this.handleThumbnailClick = this.handleThumbnailClick.bind(this)
    this.handleThumbnailKeyDown = this.handleThumbnailKeyDown.bind(this)

    this.scrollTimer = null
    // desktop - vars
    this.thumbnailContainer = this.querySelector('.js-thumbnail-container')
    this.thumbnailButtons = [...this.querySelectorAll('.js-thumbnail-button')]
    this.thumbnailListItems = [...this.querySelectorAll('.js-thumbnail')]
    this.isQuickview = this.dataset.isQuickview === 'true'

    this.galleryModal = this.querySelector('.js-gallery-modal')
    this.closeZoomButtons = [...this.querySelectorAll('.js-close-zoom')]
    this.galleryModalImages = this.querySelector('.js-gallery-modal-images')
    this.galleryModalThumbs = this.querySelector('.js-gallery-modal-thumbs')
    this.mobileSlides = [...this.querySelectorAll('.js-mobile-slide')]

    this.galleryModalMobile = this.querySelector('.js-gallery-modal-mobile')
    this.closeZoomMobileButtons = [
      ...this.querySelectorAll('.js-close-zoom-mobile'),
    ]

    this._initOptionSelector = this._initOptionSelector.bind(this)
    this.galleryModalImagesMobile = this.querySelector(
      '.js-gallery-modal-images-mobile'
    )
    this.galleryModalThumbsMobile = this.querySelector(
      '.js-gallery-modal-thumbs-mobile'
    )

    this.scrollPositions = []
    this.scrollPositionsThumbs = []
    this.doesPDPHaveStickyGallery =
      [...document.querySelectorAll('.product-gallery--sticky')].length > 0

    this.thumbnailIndex = -1

    this.visibleThumbnails = this.thumbnailListItems.filter(
      (item) => !item.classList.contains('hidden')
    )

    this.variantType = this.getAttribute('galleryType')?.trim()

    this.closeZoomModal = this.closeZoomModal.bind(this)

    this.galleryAdditionalContent =
      this.querySelector('.js-gallery-additional')?.innerHTML || ''

    // mobile bindings not required, simple slider controls nav

    this.addedKeyDownEventToModal = false
    this.currentIndexInModal = 0
    this.totalImagesInModal = 0

    // desktop - bindings
    this.thumbnailButtons.forEach((button) => {
      button.addEventListener('click', this.handleThumbnailClick)
      button.addEventListener('keydown', this.handleThumbnailKeyDown)

    }, false)

    if (this.thumbnailContainer) {
      this.zoomButtons.forEach((button) => {
        button.addEventListener('click', this.handleActiveMediaClick.bind(this))
      })

      this.zoomButtons.forEach((button) => {
        button.addEventListener(
          'keydown',
          this.handleActiveMediaClick.bind(this)
        )
      })

      this.closeZoomButtons.forEach((button) =>
        button.addEventListener(
          'click',
          this.handleCloseZoomButtonClick.bind(this)
        )
      )
    }

    this.closeZoomMobileButtons.forEach((button) =>
      button.addEventListener(
        'click',
        this.handleCloseZoomButtonClick.bind(this)
      )
    )

    this.toggleLockedButtons()
  }

  toggleLockedButtons() {
    this.swiperContainers.forEach((swiperCont) => {
      let buttons = [
        ...swiperCont.shadowRoot.querySelectorAll(
          '.swiper-button-prev, .swiper-button-next'
        ),
      ]
      buttons.forEach((button) => {
        if (button.classList.contains('swiper-button-lock')) {
          button.style.setProperty('display', 'none', 'important')
        } else {
          button.style.setProperty('display', 'block')
        }
      })
    })
  }

  async connectedCallback() {
    if (this.mainProduct) {
      this.mainProduct.registerVariantChangeCallback(this.handleVariantChange)
    }
    await this.mainProduct.isReadyPromise
    this.selectVisibleThumbs()
    this.selectFirstThumb(true)
    this.setupThumbnailObserver()
  }

  get productId() {
    return this.getAttribute('product-id')
  }

  get product() {
    return JSON.parse(
      window.atob(
        document.querySelector(`#main-product-data-${this.productId}`).innerText
      )
    )
  }

  get currentGalleryVariantId() {
    return this.getAttribute('active-variant-id')
  }

  set currentGalleryVariantId(id) {
    this.setAttribute('active-variant-id', id)
  }

  extractSlidesFromHTML(html) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const slides = [...doc.querySelectorAll('swiper-slide')]

    return slides.map((slide) => slide.outerHTML) // Convert to array of HTML elements to HTML strings
  }

  updateSwiper(swiper, slides) {
    swiper.removeAllSlides()
    swiper.addSlide(1, this.extractSlidesFromHTML(slides))
    swiper.update()
    swiper.slideTo(0, 0)
    this.zoomButtons = [
      ...this.querySelectorAll('.adaptive-image__icon.js-zoom'),
    ]
    this.zoomButtons.forEach((button) => {
      button.addEventListener('click', this.handleActiveMediaClick.bind(this))
    })
    this.thumbnailButtons = [...this.querySelectorAll('.js-thumbnail-button')]
    this.thumbnailButtons.forEach((button) => {
      button.addEventListener('click', this.handleThumbnailClick)
      button.addEventListener('keydown', this.handleThumbnailKeyDown)
    }, false)

    this.toggleLockedButtons()
  }

  async fetchImagesForVariantId(variantId) {
    if (this.variantImagesHTMLCACHE[variantId]) {
      return this.variantImagesHTMLCACHE[variantId]
    }

    const url = `${window.Shopify.routes.root}products/${this.product.handle}?variant=${variantId}&sections=main-product-gallery`

    const response = await fetch(url)
    const content = await response.json()

    if (response.status !== 200) {
      console.error('Failed to fetch images for variant', variantId)
      return null
    }

    if (!content['main-product-gallery']) {
      console.error('No gallery images found for variant', variantId)
      return null
    }

    this.variantImagesHTMLCACHE[variantId] = content['main-product-gallery']
    return this.variantImagesHTMLCACHE[variantId]
  }



  async updateVariantImages(variant) {
    if (!variant) return
    if (String(variant.id) === this.currentGalleryVariantId) return
    const variantImages = await this.fetchImagesForVariantId(String(variant.id))
    const parsedHTML = new DOMParser().parseFromString(
      variantImages,
      'text/html'
    )
    const sliderElement = parsedHTML.querySelector('swiper-product-gallery')

    if (!sliderElement) return
    this.innerHTML = sliderElement.innerHTML
    this.currentGalleryVariantId = variant.id
    this.setupThumbnailObserver()
  }

  handleVariantChange(variant) {
    if (!document.startViewTransition) {
      return this.updateVariantImages(variant)
    }

    document.startViewTransition(() => this.updateVariantImages(variant))
  }

  get variantImages() {
    return window[`variantImages${this.product.id}`]
  }

  handleThumbnailClick(event) {
    this.updateSelection({ ...event.target.dataset })
  }

  handleThumbnailKeyDown(event) {
    if (event.keyCode !== 13) {
      return
    }
    const closestSwiperSlide = event.target.closest('swiper-slide')
    this.swiperMainDesktop.swiper.slideTo(parseInt(closestSwiperSlide.dataset.swiperSlideIndex, 10) + 1)

    this.updateSelection({ ...event.target.dataset })
  }

  selectThumb(element) {
    if (element) {
      element.querySelector('button').click()
    }
  }

  selectFirstThumb(doFocus = false) {
    if (this.thumbnailContainer) {
      const currentIndex = parseInt(
        this.thumbnailContainer?.dataset.scrollIndex
      )
      let firstVisibleThumb = this.visibleThumbnails?.[currentIndex]

      if (doFocus) {
        setTimeout(function () {
          firstVisibleThumb?.focus()
        }, 150)
      } else {
        setTimeout(function () {
          firstVisibleThumb?.querySelector('button').click()
        }, 350)
      }
    }
  }

  clearVisibleThumbs() {
    this.thumbnailListItems.map((item) => {
      item.classList.remove('slide-visible')
    })
  }

  clearVisibleSlides() {
    this.mobileSlides.map((item) => {
      item.querySelector('button').setAttribute('tabindex', -1)
    })
  }

  // tracks which thumbnails are showing
  selectVisibleThumbs() {
    if (this.thumbnailContainer) {
      // 1. remove visible class from old thumbs
      this.clearVisibleThumbs()
      // 2. add visible class to five visible thumbs
      let scrollIndex = parseFloat(this.thumbnailContainer.dataset.scrollIndex)

      this.visibleThumbnails.slice(scrollIndex, scrollIndex + 5).map((item) => {
        item.classList.add('slide-visible')
      })
    }
  }

  scrollByOneThumb(direction) {
    this.clearSlideTimer()
    const currentIndex = parseInt(this.thumbnailContainer.dataset.scrollIndex)
    const visibleThumbs = this.thumbnailListItems.filter(
      (item) => !item.classList.contains('hidden')
    )

    let nextThumb

    switch (direction) {
      case 'down':
        if (currentIndex >= visibleThumbs.length - 1) {
          return
        }

        this.thumbnailContainer.dataset.scrollIndex = currentIndex + 1
        nextThumb = visibleThumbs[currentIndex + 1]

        break
      case 'up':
        if (currentIndex - 1 < 0) {
          return
        }
        this.thumbnailContainer.dataset.scrollIndex = currentIndex - 1
        nextThumb = visibleThumbs[currentIndex - 1]
    }

    this.visibleThumbnails = this.thumbnailListItems.filter(
      (item) => !item.classList.contains('hidden')
    )

    const newTopPosition = nextThumb.offsetTop
    let thumbnailContainer = this.thumbnailContainer

    thumbnailContainer.scrollTo({
      top: newTopPosition,
      behavior: 'smooth',
    })

    // Reset slide index
    let visibleThumbnails = this.visibleThumbnails
    let thumbnailOffset = this.thumbnailOffset
    let selectThumb = this.selectThumb

    if (nextThumb.classList.contains('first-clone')) {
      thumbnailContainer.dataset.scrollIndex = this.thumbnailOffset
      try {
        this.scrollTimer = setTimeout(() => {
          thumbnailContainer.scrollTo({
            top: visibleThumbnails[thumbnailOffset].offsetTop,
            behavior: 'instant',
          })
        }, 350)
        selectThumb(visibleThumbnails[thumbnailOffset])
      } catch (error) {
        console.error(error)
      }
    } else if (nextThumb.classList.contains('last-clone')) {
      thumbnailContainer.dataset.scrollIndex = this.thumbnailOffset

      try {
        this.scrollTimer = setTimeout(() => {
          thumbnailContainer.scrollTo({
            top: visibleThumbnails[thumbnailOffset].offsetTop,
            behavior: 'instant',
          })
          selectThumb(visibleThumbnails[thumbnailOffset])
        }, 350)
      } catch (error) {
        console.error(error)
      }

      setTimeout(function () {
        thumbnailContainer.scrollTo({
          top: visibleThumbnails[thumbnailOffset].offsetTop,
          behavior: 'instant',
        })
      }, 350)
    } else {
      this.selectFirstThumb()
    }
  }

  scrollByOneMobileSlide(direction) {
    let currentIndex = parseInt(this.mobileSlidesContainer.dataset.scrollIndex)

    const visibleSlides = this.mobileSlides.filter(
      (slide) => !slide.classList.contains('hidden')
    )

    let nextSlide

    switch (direction) {
      case 'right':
        if (currentIndex >= visibleSlides.length) {
          return
        }

        this.mobileSlidesContainer.dataset.scrollIndex = currentIndex + 1
        nextSlide = visibleSlides[currentIndex + 1]

        break
      case 'left':
        if (currentIndex <= 0) {
          return
        }

        this.mobileSlidesContainer.dataset.scrollIndex = currentIndex - 1
        nextSlide = visibleSlides[currentIndex - 1]
    }

    const newLeftPosition = nextSlide?.offsetLeft

    this.mobileSlidesContainer.scrollTo({
      left: newLeftPosition,
      top: 0,
      behavior: 'smooth',
    })

    let mobileOffset = this.mobileSlideOffset
    let mobileSlideContainer = this.mobileSlidesContainer

    if (nextSlide.classList.contains('first-mobile-cloned-slide')) {
      setTimeout(function () {
        mobileSlideContainer.dataset.scrollIndex =
          visibleSlides.length - 2 - mobileOffset
        mobileSlideContainer.scrollTo({
          left: visibleSlides[visibleSlides.length - mobileOffset * 2]
            .offsetLeft,
          behavior: 'instant',
        })
      }, 350)
    } else if (nextSlide.classList.contains('last-mobile-cloned-slide')) {
      setTimeout(function () {
        mobileSlideContainer.dataset.scrollIndex = mobileOffset + 1
        mobileSlideContainer.scrollTo({
          left: visibleSlides[mobileOffset + 1].offsetLeft,
          behavior: 'instant',
        })
      }, 350)
    }
  }

  handleActiveMediaClick(event) {
    // on enter key only
    if (event.type === 'keydown' && event.keyCode !== 13) {
      return
    }

    if (document.querySelector('.gift-card-gallery')) {
      return
    }

    document.body.classList.add('frozen')

    if (!this.isQuickview) {
      isMobile()
        ? this.openZoomModalMobile(
            event.target.closest('.product-gallery__media-slide-div')
          )
        : this.openZoomModal()
    }

    let toggleCloseZoom = this.handleCloseZoomButtonClick.bind(this)

    function handleEsc(e) {
      if (e.keyCode === 27) {
        toggleCloseZoom()
      }
    }

    document.addEventListener('keydown', handleEsc)
  }

  handleCloseZoomButtonClick(event) {
    document.body.classList.remove('frozen')
    this.closeZoomModal()
  }

  updateSelection(data) {
    let thumbnailIndex = parseFloat(data.thumbnailIndex)

    if (thumbnailIndex === -1) {
      console.error('Could not find thumbnail')
      return
    }

    let oldActiveItems = [
      ...this.thumbnailContainer.querySelectorAll('.active'),
    ]

    oldActiveItems.forEach((item) => item.classList.remove('active'))

    if (thumbnailIndex) {
      this.thumbnailIndex = thumbnailIndex
    } else {
      this.thumbnailListItems[0].classList.add('active')

      this.variantThumbnails = this.thumbnailListItems.filter(
        (item) =>
          !item.classList.contains('hidden') &&
          !item.classList.contains('cloned-slide')
      )
      this.thumbnailIndex = -1
      if (this.variantThumbnails.length > 0) {
        this.thumbnailIndex =
          this.variantThumbnails[0].querySelector(
            'button'
          ).dataset.thumbnailIndex
      }
    }
  }

  static get observedAttributes() {
    return ['variant']
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'variant') {
      // On variant change:
      if (!this.variantMap) {
        this._initOptionSelector()
      }

      let refinedAlt =
        this.variantMap &&
        Array.from(this.variantMap).filter(
          (variant) => variant.optionGenName === newValue
        )?.[0]?.name

      if (this.thumbnailContainer) {
        // DESKTOP

        this.thumbnailListItems.forEach((item) => {
          const variantAlt = (item?.dataset?.variantAlt?.trim() || '').replace(
            /\s-\s.+\s\d+$/i,
            ''
          )
          if (
            variantAlt !== refinedAlt &&
            variantAlt !== 'All' &&
            variantAlt !== ''
          ) {
            item.classList.add('hidden')
          } else {
            item.classList.remove('hidden')
          }
        })

        let visibleSlidesWithNoClones = this.thumbnailListItems.filter(
          (item) =>
            !item.classList.contains('hidden') &&
            !item.classList.contains('cloned-slide')
        )

        this.thumbnailOffset = visibleSlidesWithNoClones.length

        if (this.thumbnailContainer) {
          this.thumbnailContainer.dataset.scrollIndex = this.thumbnailOffset
        }

        this.visibleThumbnails = this.thumbnailListItems.filter(
          (item) => !item.classList.contains('hidden')
        )
      }
    }
  }

  generateThumbnailMarkup(data, i, totalImages) {
    return `
      <li>
        <button
          class="js-gallery-thumbnail-button"
          data-index="${i}"
          aria-label="image ${i + 1} of ${totalImages}"
        >
          <img
            alt="${data.alt}"
            src="${data.thumb}"
            loading="eager">
        </button>
      </li>
    `
  }

  updateAriaHiddenState() {
    const thumbnailsContainer = this.querySelector(
      '.product-gallery-modal__thumbnails'
    )
    if (!thumbnailsContainer) return

    const visibleAreaTop = this.galleryModal.getBoundingClientRect().top
    const visibleAreaBottom = visibleAreaTop + this.galleryModal.offsetHeight

    thumbnailsContainer
      .querySelectorAll('.js-gallery-thumbnail-button')
      .forEach((item) => {
        const rect = item.getBoundingClientRect()
        const isVisible =
          rect.top >= visibleAreaTop && rect.bottom <= visibleAreaBottom
        item.setAttribute('aria-hidden', !isVisible)
        item.setAttribute('tabindex', isVisible ? '0' : '-1')
      })
  }

  setCurrentThumbnail(currentIndex, mobile) {
    if (mobile) {
      this.muteModalMobileScroll = true
    }
    const _currentIndex = Number(currentIndex)
    let _currentItem = null
    this.querySelectorAll('.js-gallery-thumbnail-button').forEach((item) =>
      item.classList.remove('active')
    )
    this.querySelectorAll('.js-gallery-thumbnail-button').forEach(
      (item, index) => {
        item.classList[index === _currentIndex ? 'add' : 'remove']('active')
        _currentItem = item
        item.setAttribute(
          'aria-current',
          index === _currentIndex ? 'true' : 'false'
        )
      }
    )

    if (!mobile) {
      this.galleryModal.scrollTo({
        top: this.scrollPositions[_currentIndex],
        behavior: 'smooth',
      })
      // isFirstOpen &&
      //   this.galleryModalThumbs.scrollTo({
      //     top: this.scrollPositionsThumbs[_currentIndex] - 30,
      //     behavior: 'smooth',
      //   })
    } else {
      this.galleryModalImagesMobile.scrollTo({
        left: this.scrollPositions[_currentIndex],
        top: 0,
        behavior: 'smooth',
      })
      this.galleryModalThumbsMobile.scrollTo({
        left: this.scrollPositionsThumbs[_currentIndex] - 30,
        behavior: 'smooth',
      })
      this.doMuteModalMobileScroll()
    }
    _currentItem.focus()
    this.updateAriaHiddenState()
    const thumbnailsContainer = this.querySelector(
      '.product-gallery-modal__thumbnails'
    )
    thumbnailsContainer.addEventListener('scroll', () => {
      this.updateAriaHiddenState()
    })
  }

  setupThumbnailObserver() {
    const updateAriaAttributes = () => {
      this.thumbnailListItems.forEach((thumbnail) => {
        thumbnail.removeAttribute('aria-current')
        thumbnail.setAttribute('aria-hidden', 'true')

        if (thumbnail.classList.contains('swiper-slide-thumb-active')) {
          thumbnail.setAttribute('aria-current', 'true')
        }

        if (thumbnail.classList.contains('swiper-slide-fully-visible')) {
          thumbnail.setAttribute('aria-hidden', 'false')
          thumbnail.removeAttribute('tabindex')
        }
      })

      this.mobileSlides.forEach((thumbnail) => {
        thumbnail.removeAttribute('aria-current')
        thumbnail.setAttribute('aria-hidden', 'true')
        if (thumbnail.classList.contains('swiper-slide-active')) {
          thumbnail.setAttribute('aria-current', 'true')
          thumbnail.setAttribute('aria-hidden', 'false')
          thumbnail.removeAttribute('tabindex')
        }
      })
    }

    this.thumbnailListItems = document.querySelectorAll(
      '.product-gallery__thumb-item'
    )
    this.mobileSlides = document.querySelectorAll(
      '.product-gallery__media-slide'
    )

    if (this.thumbnailObserver) {
      this.thumbnailObserver.disconnect()
    }

    const observer = new MutationObserver(updateAriaAttributes)
    this.thumbnailObserver = observer

    const config = {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    }

    this.thumbnailListItems.forEach((thumbnail) => {
      observer.observe(thumbnail, config)
    })

    this.mobileSlides.forEach((thumbnail) => {
      observer.observe(thumbnail, config)
    })

    updateAriaAttributes()
  }

  // Disable scroll watcher till initlal scroll ends
  doMuteModalMobileScroll() {
    this.galleryModalImagesMobile.onscroll = (event) => {
      clearTimeout(this.scrollEndTimer)
      this.scrollEndTimer = setTimeout(() => {
        this.muteModalMobileScroll = false
        this.galleryModalImagesMobile.onscroll = null
        this.watchModalMobileScroll()
      }, 100)
    }
  }

  trapFocus(e) {
    const focusableEls = this.galleryModal.querySelectorAll(
      '.js-close-zoom, .js-gallery-thumbnail-button'
    )
    const firstFocusableEl = focusableEls[0]
    const lastFocusableEl = focusableEls[focusableEls.length - 1]
    const KEYCODE_TAB = 9

    const isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB
    const isUpPressed = e.key === 'ArrowUp' || e.keyCode === KEYCODE_UP
    const isDownPressed = e.key === 'ArrowDown' || e.keyCode === KEYCODE_DOWN

    if (isUpPressed || isDownPressed) {
      let currentIndex = this.currentIndexInModal
      if (this.currentIndexInModal === 0) {
        currentIndex = isUpPressed
          ? this.totalImagesInModal - 1
          : this.currentIndexInModal + 1
      } else if (this.currentIndexInModal === this.totalImagesInModal - 1) {
        currentIndex = isUpPressed ? this.currentIndexInModal - 1 : 0
      } else {
        currentIndex = isUpPressed
          ? this.currentIndexInModal - 1
          : this.currentIndexInModal + 1
      }

      this.setCurrentThumbnail(currentIndex, false)
      this.currentIndexInModal = currentIndex
    }

    if (!isTabPressed) {
      return
    }

    if (e.shiftKey) {
      /* shift + tab */ if (document.activeElement === firstFocusableEl) {
        lastFocusableEl.focus()
        e.preventDefault()
      }
    } /* tab */ else {
      if (document.activeElement === lastFocusableEl) {
        firstFocusableEl.focus()
        e.preventDefault()
      }
    }
  }

  openZoomModal() {
    this.lastFocusableEl = document.activeElement
    this._initModalReady(this.galleryModalImages, this.galleryModalThumbs)
  }

  openZoomModalMobile(item) {
    this.lastFocusableEl = document.activeElement
    this.muteModalMobileScroll = false
    this.thumbnailIndex = Number(item.dataset.thumbnailIndex)
    this._initModalReady(
      this.galleryModalImagesMobile,
      this.galleryModalThumbsMobile,
      true
    )
    this.muteModalMobileScroll = true
    this.addEventListeners()
    this.doMuteModalMobileScroll()
  }

  // This creates a map for easier variant switching
  _initOptionSelector() {
    if (this.variantType === 'none') {
      return
    }

    let selector = document.querySelector('.single-option-selector')
    let options = [...selector.querySelectorAll('option')]

    let liquidVariants = document.querySelector('.hidden-variants')
    let liquidOptions = [...liquidVariants.querySelectorAll('option')]

    let rawVariants = this.product.variants

    let uniqueSelector = options.map((option, index) => {
      let newObject = {
        value: rawVariants[index].id,
        name: liquidOptions[index].innerText,
        optionGenName: option.value,
      }

      return newObject
    })

    this.variantMap = uniqueSelector
  }

  _initModalReady(imagesContainer, thumbsContainer, mobile = false) {
    imagesContainer.innerHTML = ''
    thumbsContainer.innerHTML = ''

    this.classList.remove('product-gallery--sticky')

    this.scrollPositions = []
    this.scrollPositionsThumbs = []

    let variantThumbs = []
    if (mobile) {
      variantThumbs = Array.from(
        this.querySelectorAll('swiper-container.mobile .js-mobile-slide')
      ).filter((item) => !item.classList.contains('hidden'))
    } else {
      variantThumbs = Array.from(this.querySelectorAll('.js-thumbnail')).filter(
        (item) =>
          item.classList.contains('cloned-slide') !== true &&
          item.classList.contains('hidden') !== true
      )
    }

    const imagesData = []
    let thumbButton = null,
      thumbImg = null
    variantThumbs.forEach((item) => {
      thumbImg = item.querySelector('img')
      if (mobile) {
        thumbButton = item.querySelector('.product-gallery__media-slide-div')
        imagesData.push({
          id: thumbButton.dataset.thumbnailIndex,
          alt: thumbButton.dataset.alt,
          aspectRatio: thumbButton.dataset.aspectRatio ?? null,
          loading: thumbButton.dataset.loading ?? null,
          thumb: thumbButton
            ? thumbButton.dataset.thumbSrc
            : thumbButton.dataset.thumbSrc ?? null,
          src: thumbButton.dataset.previewImageUrl,
          posterImage: thumbButton.dataset.previewImageUrl,
          video: thumbButton.dataset.video ?? null,
          mediaType: thumbButton.dataset.type,
          customControls: thumbButton.dataset.customControls ?? null,
        })
      } else {
        thumbButton = item.querySelector('button')
        imagesData.push({
          id: thumbButton.dataset.thumbnailIndex,
          alt: thumbButton.dataset.alt,
          aspectRatio: thumbButton.dataset.aspectRatio ?? null,
          loading: thumbButton.dataset.loading ?? null,
          thumb: thumbImg
            ? thumbImg.dataset.src
            : thumbButton.dataset.thumbSrc ?? null,
          thumbHeight: 99 / Number(thumbImg?.dataset.ratio) || 99,
          src: thumbButton.dataset.previewImageUrl,
          posterImage: thumbButton.dataset.previewImageUrl,
          video: thumbButton.dataset.video ?? null,
          mediaType: thumbButton.dataset.type,
          customControls: thumbButton.dataset.customControls ?? null,
          loop: thumbButton.getAttribute('loop') === 'true',
        })
      }
    })

    let imagesMarkUp = ''
    imagesData.forEach((item) => {
      if (item.video) {
        if (
          item.mediaType === 'external_video' ||
          typeof item.mediaType === 'undefined'
        ) {
          imagesMarkUp += generateExternalVideoMarkup(item, true)
        } else {
          imagesMarkUp += generateVideoMarkup(item, true, true)
        }
      } else {
        imagesMarkUp += generateImageMarkup(item)
      }
    })

    imagesContainer.innerHTML = imagesMarkUp

    let thumbsMarkUp = ''
    imagesData.forEach((item, i) => {
      thumbsMarkUp += this.generateThumbnailMarkup(item, i, imagesData.length)
    })
    thumbsContainer.innerHTML = thumbsMarkUp

    if (mobile) {
      this.galleryModalMobile.classList.add('opened')
    } else {
      this.galleryModal.classList.add('m-block')
    }

    let currentIndex = 0
    imagesData.forEach((item, i) => {
      if (Number(item.id) === Number(this.thumbnailIndex)) {
        currentIndex = i
      }
    })

    setTimeout(() => {
      this.scrollPositions = Array.from(imagesContainer.children).reduce(
        (positions, item) => {
          mobile
            ? positions.push(item.offsetLeft)
            : positions.push(item.offsetTop)
          return positions
        },
        []
      )

      this.scrollPositionsThumbs = Array.from(thumbsContainer.children).reduce(
        (positions, item) => {
          mobile
            ? positions.push(item.offsetLeft)
            : positions.push(item.offsetTop)
          return positions
        },
        []
      )

      this.setCurrentThumbnail(currentIndex, mobile, true)
      this.currentIndexInModal = currentIndex

      if (!mobile) {
        this.galleryModal.addEventListener('keydown', this.trapFocus.bind(this))
      }
    }, 200)

    this.querySelectorAll('.js-gallery-thumbnail-button').forEach((item) => {
      item.addEventListener(
        'click',
        (e) => {
          this.setCurrentThumbnail(item.dataset.index, mobile)
          this.currentIndexInModal = Number(item.dataset.index)
        },
        false
      )
    })
  }

  closeZoomModal() {
    this.galleryModalImages.innerHTML = ''
    this.galleryModalThumbs.innerHTML = ''
    this.galleryModal.classList.remove('m-block')

    this.galleryModalImagesMobile.innerHTML = ''
    this.galleryModalThumbsMobile.innerHTML = ''
    this.galleryModalMobile.classList.remove('opened')

    if (this.doesPDPHaveStickyGallery) {
      this.classList.add('product-gallery--sticky')
    }

    if (this.lastFocusableEl) {
      this.lastFocusableEl.focus()
      this.lastFocusableEl = null
    }

    this.galleryModal.removeEventListener('keydown', this.trapFocus.bind(this))
  }

  addEventListeners() {
    this.galleryModalImagesMobile.addEventListener(
      'scroll',
      this.watchModalMobileScroll.bind(this)
    )
  }

  removeEventListeners() {
    this.galleryModal.removeEventListener('keydown', this.trapFocus.bind(this))
    this.galleryModalImagesMobile.removeEventListener(
      'scroll',
      this.watchModalMobileScroll.bind(this)
    )
  }

  watchModalMobileScroll() {
    if (this.muteModalMobileScroll) return
    this.setCurrentThumbnail(
      this.scrollPositions.indexOf(
        [...this.scrollPositions].sort((a, b) => {
          return (
            Math.abs(this.galleryModalImagesMobile.scrollLeft - a) -
            Math.abs(this.galleryModalImagesMobile.scrollLeft - b)
          )
        })[0]
      ),
      true
    )
  }
}

window.customElements.define('swiper-product-gallery', SwiperProductGallery)
